// ─────────────────────────────────────────────────────────────────────────────
// emailLinks.js — build mailto: links for invoices & quotations (no backend).
// Opens the user's default mail client pre-filled with To / Subject / Body.
// ─────────────────────────────────────────────────────────────────────────────
// ⛔ SEND-0 — NO FALLBACK BUSINESS IMPORT HERE, DELIBERATELY. Both builders below
// used to merge invoicePdf's DEFAULT_BUSINESS under the caller's business, which
// put Msquared's name and accounts@ address into a mail addressed to a tenant's
// customer. Both are dormant today, but SEND-1 wakes emailInvoice — and leaving
// emailQuotation would have left a live leak sitting behind a future button.
// A missing name or email now drops the phrase that needed it.

import { PAYMENT_TERMS } from "./invoices";
import { businessFromSnapshot } from "../invoices/invoicePdfAdapter";

// SEND-1 §2.3 — the currency was hardcoded to €, so a GBP or USD invoice emailed
// its customer euro figures. Mirrors the ternary in InvoiceView.jsx and
// InvoiceManager.jsx. ⚠️ That makes three copies of this mapping; a shared helper
// in invoices.js beside CURRENCIES is the right end state and is a follow-up, not
// this brief.
const currencySymbol = (code) => (code === "GBP" ? "£" : code === "USD" ? "$" : "€");
const money = (n, cur = "€") => `${cur}${(Number(n) || 0).toFixed(2)}`;

// ⛔ SEND-1 §2.4 — there was a SECOND, DRIFTED copy of the terms table here. It
// had `immediate` where invoices.js has `due_on_receipt`, and it was missing
// `net_90` entirely, so both of those fell through and printed the raw key
// ("net_90") to the customer. Derived from the real PAYMENT_TERMS now, so a term
// added there can never again be missing here.
const termLabel = (v) => PAYMENT_TERMS.find((t) => t.value === v)?.label || v;

// ⛔ SEND-1 §2.2 — this read `invoice.items`, which WP9 invoices do not carry.
// They carry `invoice_lines` with `line_total`, so EVERY real invoice printed
// "(see attached details)" instead of its lines. Accepts either shape: invoices
// pass invoice_lines (line_total), quotations still pass items (total).
function itemLines(lines, cur = "€") {
  if (!lines || !lines.length) return "  (see attached details)";
  return lines.map((l) => {
    const qty = l.quantity || 0;
    const total = l.line_total != null ? l.line_total
      : l.total != null ? l.total
      : qty * (l.unit_price || 0);
    return `  - ${l.description || "Item"} x${qty} @ ${money(l.unit_price, cur)} = ${money(total, cur)}`;
  }).join("\n");
}

// §2 — "Include the bank details (account name, IBAN, BIC) when present, since
// that's how the customer pays." Omitted entirely when there is nothing to pay
// into, and each row omitted individually, on the SEND-0 rule: print the line or
// omit the line, never a label with nothing after it.
function bankBlock(b) {
  const rows = [
    b.bank_account_name ? `  Account Name: ${b.bank_account_name}` : null,
    b.iban ? `  IBAN: ${b.iban}` : null,
    b.bic ? `  BIC: ${b.bic}` : null,
  ].filter(Boolean);
  return rows.length ? `\n\nPayment details:\n${rows.join("\n")}` : "";
}

// Build the mailto: URL and open it via a transient anchor (reliable across
// browsers; won't blank the current page the way window.open(mailto) can).
export function openMailto(to, subject, body) {
  const url = `mailto:${encodeURIComponent(to || "")}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  const a = document.createElement("a");
  a.href = url;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  return url;
}

// ── SEND-1 — the invoice is the ONLY argument ───────────────────────────────
// §2: the business block is derived from the invoice's own frozen
// business_details / bank_details through businessFromSnapshot, the same
// normaliser the PDF uses. There is no `business` parameter to pass the wrong
// thing to, and no fallback object anywhere: a missing trading name falls back to
// legal_name (inside businessFromSnapshot) and then to omitting the sign-off
// name, and a missing business email omits the "contact us at" sentence.
function invoiceParts(invoice) {
  const b = businessFromSnapshot(invoice.business_details, invoice.bank_details);
  return {
    b,
    cur: currencySymbol(invoice.preferred_currency),
    lines: invoice.invoice_lines || invoice.lines || [],
    num: invoice.invoice_number || "",
    contact: b.email
      ? `\n\nIf you have any questions about this invoice, please don't hesitate to contact us at ${b.email}.`
      : "",
    signature: b.name ? `\n${b.name}` : "",
  };
}

export function emailInvoice(invoice) {
  const { b, cur, lines, num, contact, signature } = invoiceParts(invoice);
  const subject = b.name ? `Invoice ${num} from ${b.name}` : `Invoice ${num}`;
  const due = invoice.due_date ? `\nDue Date: ${invoice.due_date}` : "";
  const terms = invoice.payment_terms ? `\nPayment Terms: ${termLabel(invoice.payment_terms)}` : "";
  const body =
`Dear ${invoice.customer_name || "Customer"},

Please find the details of your invoice below.

Invoice: ${num}
Issue Date: ${invoice.issue_date || "-"}${due}
Amount Due: ${money(invoice.total_amount, cur)}${terms}

Items:
${itemLines(lines, cur)}${bankBlock(b)}${contact}

Kind regards,${signature}`;
  return openMailto(invoice.customer_email, subject, body);
}

/**
 * SEND-1 §3 — the overdue variant. Same invoice, different opening: it leads with
 * the due date and what is STILL OWED.
 *
 * ⛔ amount_outstanding, NEVER total_amount. On a part-paid invoice the total is
 * not the debt, and chasing a customer for money they have already paid is worse
 * than not chasing them at all. total_amount is not printed here at all.
 */
export function emailInvoiceReminder(invoice) {
  const { b, cur, lines, num, contact, signature } = invoiceParts(invoice);
  const subject = `Reminder: Invoice ${num} is overdue`;
  // The ledger view supplies amount_outstanding; fall back to the total only when
  // the field is genuinely absent (a plain header read), never when it is 0.
  const outstanding = invoice.amount_outstanding != null ? invoice.amount_outstanding : invoice.total_amount;
  const terms = invoice.payment_terms ? `\nPayment Terms: ${termLabel(invoice.payment_terms)}` : "";
  const body =
`Dear ${invoice.customer_name || "Customer"},

Invoice ${num} was due on ${invoice.due_date || "-"} and ${money(outstanding, cur)} is still outstanding.

Invoice: ${num}
Issue Date: ${invoice.issue_date || "-"}
Due Date: ${invoice.due_date || "-"}
Amount Outstanding: ${money(outstanding, cur)}${terms}

Items:
${itemLines(lines, cur)}${bankBlock(b)}${contact}

If this has already been paid, please ignore this reminder.

Kind regards,${signature}`;
  return openMailto(invoice.customer_email, subject, body);
}

export function emailQuotation(quotation, business) {
  const b = business || {};
  const num = quotation.quotation_number || "";
  const subject = b.name ? `Quotation ${num} from ${b.name}` : `Quotation ${num}`;
  const valid = quotation.valid_until ? `\nValid Until: ${quotation.valid_until}` : "";
  const contact = b.email
    ? `\n\nIf you would like to proceed or have any questions, please don't hesitate to contact us at ${b.email}.`
    : "";
  const signature = b.name ? `\n${b.name}` : "";
  const body =
`Dear ${quotation.customer_name || "Customer"},

Thank you for your enquiry. Please find your quotation details below.

Quotation: ${num}
Issue Date: ${quotation.issue_date || "-"}${valid}
Total: ${money(quotation.total_amount)}

Items:
${itemLines(quotation.items)}${contact}

Kind regards,${signature}`;
  return openMailto(quotation.customer_email, subject, body);
}
